import React from "react";
import Svg, { Path } from "react-native-svg";

// ─────────────────────────────────────────────────────────────────────────────
// Parent tab-bar icons — the designer's own set, traced verbatim from the
// supplied 24×24 SVGs (Home / Library / Controls / Stats / Me).
//
// These replaced iOS SF Symbols plus a lucide fallback for Android and web,
// which meant the bar drew a different glyph set on each platform. One vector
// set via react-native-svg renders identically everywhere — iOS, Android, and
// the RN-Web bundle that Tizen and webOS also run.
//
// The source files hard-code a fill; that's replaced by the `color` prop so the
// bar can tint selected vs unselected itself. These are outline-weight glyphs,
// so a tint alone is a subtle selected state — the bar also bumps the size and
// draws its glass "lens" behind the active tab.
// ─────────────────────────────────────────────────────────────────────────────

export type TabIconProps = {
  size?: number;
  color?: string;
};

const DEFAULT_SIZE = 24;
const DEFAULT_COLOR = "#292D32";

function IconFrame({
  size = DEFAULT_SIZE,
  children,
}: {
  size?: number;
  children: React.ReactNode;
}) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      {children}
    </Svg>
  );
}

export function HomeIcon({ size, color = DEFAULT_COLOR }: TabIconProps) {
  return (
    <IconFrame size={size}>
      <Path
        d="M12 18.75C11.59 18.75 11.25 18.41 11.25 18V15C11.25 14.59 11.59 14.25 12 14.25C12.41 14.25 12.75 14.59 12.75 15V18C12.75 18.41 12.41 18.75 12 18.75Z"
        fill={color}
      />
      <Path
        d="M17.6 22.5599H6.39996C4.57996 22.5599 2.91996 21.1599 2.61996 19.3699L1.28996 11.3999C1.06996 10.1599 1.67996 8.56994 2.66996 7.77994L9.59996 2.22994C10.94 1.14994 13.05 1.15994 14.4 2.23994L21.33 7.77994C22.31 8.56994 22.91 10.1599 22.71 11.3999L21.38 19.3599C21.08 21.1299 19.38 22.5599 17.6 22.5599ZM11.99 2.92994C11.46 2.92994 10.93 3.08994 10.54 3.39994L3.60996 8.95994C3.04996 9.40994 2.64996 10.4499 2.76996 11.1599L4.09996 19.1199C4.27996 20.1699 5.32996 21.0599 6.39996 21.0599H17.6C18.67 21.0599 19.72 20.1699 19.9 19.1099L21.23 11.1499C21.34 10.4499 20.94 9.38994 20.39 8.94994L13.46 3.40994C13.06 3.08994 12.52 2.92994 11.99 2.92994Z"
        fill={color}
      />
    </IconFrame>
  );
}

export function LibraryIcon({ size, color = DEFAULT_COLOR }: TabIconProps) {
  return (
    <IconFrame size={size}>
      <Path
        d="M17 22.75H7C3.35 22.75 1.25 20.65 1.25 17V13C1.25 9.4 3.17003 8.06 4.78003 7.56C5.43003 7.35 6.18 7.25 7 7.25H17C17.82 7.25 18.57 7.36001 19.23 7.57001C20.83 8.06001 22.75 9.41 22.75 13V17C22.75 20.65 20.65 22.75 17 22.75ZM7 8.75C6.34 8.75 5.73998 8.83 5.22998 9C3.55998 9.51 2.75 10.82 2.75 13V17C2.75 19.86 4.14 21.25 7 21.25H17C19.86 21.25 21.25 19.86 21.25 17V13C21.25 10.82 20.44 9.51 18.78 9C18.26 8.83 17.66 8.75 17 8.75H7Z"
        fill={color}
      />
      <Path
        d="M19 9.03003C18.92 9.03003 18.85 9.01999 18.77 8.98999C18.26 8.82999 17.66 8.73999 17 8.73999H7C6.34 8.73999 5.73998 8.81999 5.22998 8.98999C4.99998 9.05999 4.75 9.02 4.56 8.88C4.37 8.74 4.25 8.51002 4.25 8.27002V7C4.25 5.48 5.48 4.25 7 4.25H17C18.52 4.25 19.75 5.48 19.75 7V8.28003C19.75 8.52003 19.64 8.74001 19.44 8.89001C19.31 8.98001 19.16 9.03003 19 9.03003ZM7 5.75C6.31 5.75 5.75 6.31 5.75 7V7.34003C6.14 7.28003 6.56 7.25 7 7.25H17C17.44 7.25 17.86 7.28003 18.25 7.34003V7C18.25 6.31 17.69 5.75 17 5.75H7Z"
        fill={color}
      />
      <Path
        d="M16 5.75H8C7.59 5.75 7.25 5.41 7.25 5V3.51001C7.25 2.26001 8.26001 1.25 9.51001 1.25H14.49C15.74 1.25 16.75 2.26001 16.75 3.51001V5C16.75 5.41 16.41 5.75 16 5.75ZM8.75 4.25H15.25V3.51001C15.25 3.09001 14.91 2.75 14.49 2.75H9.51001C9.09001 2.75 8.75 3.09001 8.75 3.51001V4.25Z"
        fill={color}
      />
      <Path
        d="M9.76998 19.5711C9.34998 19.5711 8.95998 19.4711 8.60998 19.2711C7.80998 18.8111 7.34998 17.8711 7.34998 16.6811V13.7211C7.34998 12.5411 7.80998 11.5911 8.60998 11.1311C9.40998 10.6711 10.45 10.7411 11.48 11.3411L14.05 12.8211C15.07 13.4111 15.66 14.2811 15.66 15.2011C15.66 16.1211 15.07 16.9911 14.05 17.5811L11.48 19.0611C10.9 19.4011 10.31 19.5711 9.76998 19.5711ZM9.77998 12.3311C9.61998 12.3311 9.47998 12.3611 9.36998 12.4311C9.04998 12.6211 8.85998 13.0911 8.85998 13.7211V16.6811C8.85998 17.3111 9.03998 17.7811 9.36998 17.9711C9.68998 18.1611 10.19 18.0811 10.74 17.7611L13.31 16.2811C13.86 15.9611 14.17 15.5711 14.17 15.2011C14.17 14.8311 13.86 14.4311 13.31 14.1211L10.74 12.6411C10.38 12.4311 10.05 12.3311 9.77998 12.3311Z"
        fill={color}
      />
    </IconFrame>
  );
}

export function ControlsIcon({ size, color = DEFAULT_COLOR }: TabIconProps) {
  return (
    <IconFrame size={size}>
      <Path
        d="M12 22.7599C10.91 22.7599 9.83002 22.4399 8.98002 21.8099L4.68002 18.5999C3.54002 17.7499 2.65002 15.9699 2.65002 14.5599V7.11994C2.65002 5.57994 3.78002 3.93994 5.23002 3.39994L10.22 1.52994C11.21 1.15994 12.77 1.15994 13.76 1.52994L18.75 3.39994C20.2 3.93994 21.33 5.57994 21.33 7.11994V14.5499C21.33 15.9699 20.44 17.7399 19.3 18.5899L15 21.7999C14.17 22.4399 13.09 22.7599 12 22.7599ZM10.75 2.93994L5.76002 4.80994C4.91002 5.12994 4.16002 6.20994 4.16002 7.12994V14.5599C4.16002 15.5099 4.83002 16.8399 5.58002 17.3999L9.88002 20.6099C11.03 21.4699 12.97 21.4699 14.13 20.6099L18.43 17.3999C19.19 16.8299 19.85 15.5099 19.85 14.5599V7.11994C19.85 6.20994 19.1 5.12994 18.25 4.79994L13.26 2.92994C12.58 2.68994 11.42 2.68994 10.75 2.93994Z"
        fill={color}
      />
      <Path
        d="M10.66 14.23C10.47 14.23 10.28 14.16 10.13 14.01L8.51999 12.4C8.22999 12.11 8.22999 11.63 8.51999 11.34C8.80999 11.05 9.28999 11.05 9.57999 11.34L10.66 12.42L14.43 8.65C14.72 8.36 15.2 8.36 15.49 8.65C15.78 8.94 15.78 9.42 15.49 9.71L11.19 14.01C11.04 14.16 10.85 14.23 10.66 14.23Z"
        fill={color}
      />
    </IconFrame>
  );
}

export function StatsIcon({ size, color = DEFAULT_COLOR }: TabIconProps) {
  return (
    <IconFrame size={size}>
      <Path
        d="M21 22.75H3C2.59 22.75 2.25 22.41 2.25 22C2.25 21.59 2.59 21.25 3 21.25H21C21.41 21.25 21.75 21.59 21.75 22C21.75 22.41 21.41 22.75 21 22.75Z"
        fill={color}
      />
      <Path
        d="M5.59998 19.75H4C3.04 19.75 2.25 18.96 2.25 18V9.38C2.25 8.42 3.04 7.63 4 7.63H5.59998C6.55998 7.63 7.34998 8.42 7.34998 9.38V18C7.34998 18.96 6.55998 19.75 5.59998 19.75ZM4 9.12C3.86 9.12 3.75 9.23 3.75 9.37V18C3.75 18.14 3.86 18.25 4 18.25H5.59998C5.73998 18.25 5.84998 18.14 5.84998 18V9.38C5.84998 9.24 5.73998 9.13 5.59998 9.13H4V9.12Z"
        fill={color}
      />
      <Path
        d="M12.8002 19.7499H11.2002C10.2402 19.7499 9.4502 18.9599 9.4502 17.9999V6.18994C9.4502 5.22994 10.2402 4.43994 11.2002 4.43994H12.8002C13.7602 4.43994 14.5502 5.22994 14.5502 6.18994V17.9999C14.5502 18.9599 13.7602 19.7499 12.8002 19.7499ZM11.2002 5.93994C11.0602 5.93994 10.9502 6.04994 10.9502 6.18994V17.9999C10.9502 18.1399 11.0602 18.2499 11.2002 18.2499H12.8002C12.9402 18.2499 13.0502 18.1399 13.0502 17.9999V6.18994C13.0502 6.04994 12.9402 5.93994 12.8002 5.93994H11.2002Z"
        fill={color}
      />
      <Path
        d="M19.9999 19.75H18.3999C17.4399 19.75 16.6499 18.96 16.6499 18V3C16.6499 2.04 17.4399 1.25 18.3999 1.25H19.9999C20.9599 1.25 21.7499 2.04 21.7499 3V18C21.7499 18.96 20.9599 19.75 19.9999 19.75ZM18.3999 2.75C18.2599 2.75 18.1499 2.86 18.1499 3V18C18.1499 18.14 18.2599 18.25 18.3999 18.25H19.9999C20.1399 18.25 20.2499 18.14 20.2499 18V3C20.2499 2.86 20.1399 2.75 19.9999 2.75H18.3999Z"
        fill={color}
      />
    </IconFrame>
  );
}

export function MeIcon({ size, color = DEFAULT_COLOR }: TabIconProps) {
  return (
    <IconFrame size={size}>
      <Path
        d="M12.12 13.53C12.1 13.53 12.07 13.53 12.05 13.53C12.02 13.53 11.98 13.53 11.95 13.53C9.67998 13.46 7.97998 11.69 7.97998 9.50998C7.97998 7.28998 9.78998 5.47998 12.01 5.47998C14.23 5.47998 16.04 7.28998 16.04 9.50998C16.03 11.7 14.32 13.46 12.15 13.53C12.13 13.53 12.13 13.53 12.12 13.53ZM12 6.96998C10.6 6.96998 9.46998 8.10998 9.46998 9.49998C9.46998 10.87 10.54 11.98 11.9 12.03C11.93 12.02 12.03 12.02 12.13 12.03C13.47 11.96 14.52 10.86 14.53 9.49998C14.53 8.10998 13.4 6.96998 12 6.96998Z"
        fill={color}
      />
      <Path
        d="M12 22.7499C9.30996 22.7499 6.73996 21.7499 4.74996 19.9299C4.56996 19.7699 4.48996 19.5299 4.50996 19.2999C4.63996 18.1099 5.37996 16.9999 6.60996 16.1799C9.58996 14.1999 14.42 14.1999 17.39 16.1799C18.62 17.0099 19.36 18.1099 19.49 19.2999C19.52 19.5399 19.43 19.7699 19.25 19.9299C17.26 21.7499 14.69 22.7499 12 22.7499ZM6.07996 19.0999C7.73996 20.4899 9.82996 21.2499 12 21.2499C14.17 21.2499 16.26 20.4899 17.92 19.0999C17.74 18.4899 17.26 17.8999 16.55 17.4199C14.09 15.7799 9.91996 15.7799 7.43996 17.4199C6.72996 17.8999 6.25996 18.4899 6.07996 19.0999Z"
        fill={color}
      />
      <Path
        d="M12 22.75C6.07 22.75 1.25 17.93 1.25 12C1.25 6.07 6.07 1.25 12 1.25C17.93 1.25 22.75 6.07 22.75 12C22.75 17.93 17.93 22.75 12 22.75ZM12 2.75C6.9 2.75 2.75 6.9 2.75 12C2.75 17.1 6.9 21.25 12 21.25C17.1 21.25 21.25 17.1 21.25 12C21.25 6.9 17.1 2.75 12 2.75Z"
        fill={color}
      />
    </IconFrame>
  );
}
